import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useModalContext } from "../../room/ModalContext";
import { ParticipantMuteConfirmDialog } from "./ParticipantMuteConfirmDialog";
import { registerParticipantLayoutDebugApi } from "./participant-layout-debug";
import { ParticipantLayout } from "./ParticipantLayout";
import { useParticipantLayoutMockState } from "./useParticipantLayoutMockState";
import { useParticipantLayoutRoomState } from "./useParticipantLayoutRoomState";
import {
  getParticipantLayoutScreenShareOwnerId,
  isParticipantLayoutScreenShareId
} from "./participant-layout-participants";

const PARTICIPANT_LAYOUT_SCENE_RENDER_SUPPRESSION_SOURCE = "participant-layout";
const PARTICIPANT_LAYOUT_ROOM_SPOTLIGHT_CLEANUP_DELAY_MS = 1500;
const PARTICIPANT_LAYOUT_SCREEN_SHARE_SPOTLIGHT_CLEANUP_DELAY_MS = 10000;

function getAppObject() {
  if (typeof window !== "undefined") {
    return window.APP;
  }

  if (typeof global !== "undefined") {
    return global.APP;
  }

  return undefined;
}

function setParticipantLayoutFlatVoiceAudioEnabled(enabled) {
  getAppObject()?.setParticipantLayoutFlatVoiceAudioEnabled?.(enabled);
}

function setMainSceneRenderSuppressed(suppressed) {
  getAppObject()?.setMainSceneRenderSuppressed?.(suppressed, PARTICIPANT_LAYOUT_SCENE_RENDER_SUPPRESSION_SOURCE);
}

function setMainSceneInteractionSuppressed(suppressed) {
  getAppObject()?.setMainSceneInteractionSuppressed?.(suppressed, PARTICIPANT_LAYOUT_SCENE_RENDER_SUPPRESSION_SOURCE);
}

export function isParticipantLayoutImmersiveModeActive(scene) {
  if (!scene) {
    return false;
  }

  if (scene.renderer?.xr?.isPresenting) {
    return true;
  }

  if (typeof scene.is !== "function") {
    return false;
  }

  return scene.is("vr-mode") || scene.is("ar-mode");
}

export function useParticipantLayoutImmersiveMode(scene) {
  const [immersiveModeActive, setImmersiveModeActive] = useState(() => isParticipantLayoutImmersiveModeActive(scene));

  useEffect(() => {
    if (!scene?.addEventListener || !scene?.removeEventListener) {
      setImmersiveModeActive(isParticipantLayoutImmersiveModeActive(scene));
      return undefined;
    }

    const syncImmersiveMode = () => {
      setImmersiveModeActive(isParticipantLayoutImmersiveModeActive(scene));
    };
    const eventNames = ["enter-vr", "exit-vr", "enter-ar", "exit-ar"];

    syncImmersiveMode();
    eventNames.forEach(eventName => scene.addEventListener(eventName, syncImmersiveMode));

    return () => {
      eventNames.forEach(eventName => scene.removeEventListener(eventName, syncImmersiveMode));
    };
  }, [scene]);

  return immersiveModeActive;
}

function canUseRoomParticipantLayoutState({ scene, hubChannel }) {
  return Boolean(
    scene?.addEventListener &&
      scene?.removeEventListener &&
      hubChannel?.addEventListener &&
      hubChannel?.removeEventListener &&
      hubChannel?.can &&
      getAppObject()?.mediaDevicesManager
  );
}

function useParticipantLayoutEnabledState({ enabled: controlledEnabled, onEnabledChange, forcedEnabled = false } = {}) {
  const isControlled = controlledEnabled !== undefined;
  const [uncontrolledEnabled, setUncontrolledEnabled] = useState(() => Boolean(forcedEnabled));
  const enabled = forcedEnabled || (isControlled ? Boolean(controlledEnabled) : uncontrolledEnabled);

  useEffect(() => {
    if (forcedEnabled) {
      setUncontrolledEnabled(true);
    }
  }, [forcedEnabled]);

  const setEnabled = useCallback(
    nextEnabledOrUpdater => {
      if (forcedEnabled) {
        if (!isControlled) {
          setUncontrolledEnabled(true);
        }
        return;
      }

      const nextEnabled =
        typeof nextEnabledOrUpdater === "function" ? nextEnabledOrUpdater(enabled) : Boolean(nextEnabledOrUpdater);

      if (!isControlled) {
        setUncontrolledEnabled(nextEnabled);
      }

      onEnabledChange?.(nextEnabled);
    },
    [enabled, forcedEnabled, isControlled, onEnabledChange]
  );

  const enable = useCallback(() => {
    setEnabled(true);
  }, [setEnabled]);

  const disable = useCallback(() => {
    setEnabled(false);
  }, [setEnabled]);

  const toggleEnabled = useCallback(() => {
    setEnabled(currentEnabled => !currentEnabled);
  }, [setEnabled]);

  return {
    enabled,
    setEnabled,
    enable,
    disable,
    toggleEnabled
  };
}

function useParticipantMuteConfirmation(toggleParticipantMuted) {
  const modalContext = useModalContext() || {};
  const { showNonHistoriedDialog } = modalContext;

  return useCallback(
    participant => {
      if (!participant) {
        return;
      }

      if (showNonHistoriedDialog) {
        showNonHistoriedDialog(ParticipantMuteConfirmDialog, {
          participant,
          onConfirm: () => toggleParticipantMuted(participant.id)
        });
        return;
      }

      toggleParticipantMuted(participant.id);
    },
    [showNonHistoriedDialog, toggleParticipantMuted]
  );
}

function hasParticipantLayoutSpotlightTarget(participants, spotlightTileId) {
  return Boolean(spotlightTileId && participants.some(participant => participant.id === spotlightTileId));
}

function getParticipantLayoutSpotlightCleanupDelay(participants, spotlightTileId) {
  if (!isParticipantLayoutScreenShareId(spotlightTileId)) {
    return PARTICIPANT_LAYOUT_ROOM_SPOTLIGHT_CLEANUP_DELAY_MS;
  }

  const ownerId = getParticipantLayoutScreenShareOwnerId(spotlightTileId);
  const ownerStillPresent = !ownerId || hasParticipantLayoutSpotlightTarget(participants, ownerId);

  return ownerStillPresent
    ? PARTICIPANT_LAYOUT_SCREEN_SHARE_SPOTLIGHT_CLEANUP_DELAY_MS
    : PARTICIPANT_LAYOUT_ROOM_SPOTLIGHT_CLEANUP_DELAY_MS;
}

function ParticipantLayoutRuntime({
  enabled,
  mode,
  participants,
  showParticipants,
  mySessionId,
  zoomPinnedParticipant,
  debugApi,
  onRequestClose,
  requestParticipantMute,
  toggleParticipantMuted,
  toggleParticipantPinned,
  toggleParticipantRoomSpotlight
}) {
  const sceneRenderSuppressedRef = useRef(false);

  useEffect(() => registerParticipantLayoutDebugApi(debugApi), [debugApi]);

  useEffect(() => {
    if (enabled && !sceneRenderSuppressedRef.current) {
      setParticipantLayoutFlatVoiceAudioEnabled(true);
      setMainSceneRenderSuppressed(true);
      setMainSceneInteractionSuppressed(true);
      sceneRenderSuppressedRef.current = true;
      return;
    }

    if (!enabled && sceneRenderSuppressedRef.current) {
      setParticipantLayoutFlatVoiceAudioEnabled(false);
      setMainSceneRenderSuppressed(false);
      setMainSceneInteractionSuppressed(false);
      sceneRenderSuppressedRef.current = false;
    }
  }, [enabled]);

  useEffect(
    () => () => {
      if (sceneRenderSuppressedRef.current) {
        setParticipantLayoutFlatVoiceAudioEnabled(false);
        setMainSceneRenderSuppressed(false);
        setMainSceneInteractionSuppressed(false);
        sceneRenderSuppressedRef.current = false;
      }
    },
    []
  );

  return (
    <ParticipantLayout
      enabled={enabled}
      mode={mode}
      participants={participants}
      showParticipants={showParticipants}
      mySessionId={mySessionId}
      zoomPinnedParticipant={zoomPinnedParticipant}
      onRequestClose={onRequestClose}
      onRequestParticipantMute={requestParticipantMute}
      onToggleParticipantMuted={toggleParticipantMuted}
      onToggleParticipantPinned={toggleParticipantPinned}
      onToggleParticipantRoomSpotlight={toggleParticipantRoomSpotlight}
    />
  );
}

ParticipantLayoutRuntime.propTypes = {
  enabled: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["auto", "grid", "spotlight"]).isRequired,
  participants: PropTypes.array.isRequired,
  showParticipants: PropTypes.bool.isRequired,
  mySessionId: PropTypes.string,
  zoomPinnedParticipant: PropTypes.bool,
  debugApi: PropTypes.object.isRequired,
  onRequestClose: PropTypes.func,
  requestParticipantMute: PropTypes.func.isRequired,
  toggleParticipantMuted: PropTypes.func.isRequired,
  toggleParticipantPinned: PropTypes.func.isRequired,
  toggleParticipantRoomSpotlight: PropTypes.func
};

function MockParticipantLayoutContainer({
  enabled,
  onEnabledChange,
  forcedEnabled,
  entered = true,
  zoomPinnedParticipant
}) {
  const enabledState = useParticipantLayoutEnabledState({ enabled, onEnabledChange, forcedEnabled });
  const { mode, participants, debugControls, toggleParticipantMuted, toggleParticipantPinned } =
    useParticipantLayoutMockState();
  const requestParticipantMute = useParticipantMuteConfirmation(toggleParticipantMuted);
  const showParticipants = Boolean(entered);

  const debugApi = useMemo(
    () => ({
      enable: enabledState.enable,
      disable: enabledState.disable,
      toggleEnabled: enabledState.toggleEnabled,
      ...debugControls
    }),
    [debugControls, enabledState.disable, enabledState.enable, enabledState.toggleEnabled]
  );

  return (
    <ParticipantLayoutRuntime
      enabled={enabledState.enabled}
      mode={mode}
      participants={participants}
      showParticipants={showParticipants}
      zoomPinnedParticipant={zoomPinnedParticipant}
      onRequestClose={forcedEnabled ? undefined : enabledState.disable}
      debugApi={debugApi}
      requestParticipantMute={requestParticipantMute}
      toggleParticipantMuted={toggleParticipantMuted}
      toggleParticipantPinned={toggleParticipantPinned}
    />
  );
}

MockParticipantLayoutContainer.propTypes = {
  enabled: PropTypes.bool,
  onEnabledChange: PropTypes.func,
  forcedEnabled: PropTypes.bool,
  entered: PropTypes.bool,
  zoomPinnedParticipant: PropTypes.bool
};

function RoomParticipantLayoutContainer({
  enabled,
  onEnabledChange,
  forcedEnabled,
  entered = true,
  presences,
  mySessionId,
  scene,
  hubChannel,
  performConditionalSignIn,
  roomSpotlightTileId,
  onRoomSpotlightChange,
  zoomPinnedParticipant
}) {
  const enabledState = useParticipantLayoutEnabledState({ enabled, onEnabledChange, forcedEnabled });
  const immersiveModeActive = useParticipantLayoutImmersiveMode(scene);
  const layoutEnabled = enabledState.enabled && !immersiveModeActive;
  const showParticipants = Boolean(entered);
  const {
    mode,
    participants,
    debugControls,
    toggleParticipantMuted,
    toggleParticipantPinned,
    toggleParticipantRoomSpotlight
  } = useParticipantLayoutRoomState({
    enabled: layoutEnabled && showParticipants,
    presences,
    mySessionId,
    scene,
    hubChannel,
    performConditionalSignIn,
    roomSpotlightTileId,
    onRoomSpotlightChange
  });
  const requestParticipantMute = useParticipantMuteConfirmation(toggleParticipantMuted);
  const latestParticipantsRef = useRef(participants);
  const canManageRoomSpotlight = Boolean(hubChannel?.can?.("update_hub") && onRoomSpotlightChange);

  useEffect(() => {
    latestParticipantsRef.current = participants;
  }, [participants]);

  useEffect(() => {
    if (
      !roomSpotlightTileId ||
      !canManageRoomSpotlight ||
      hasParticipantLayoutSpotlightTarget(participants, roomSpotlightTileId)
    ) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (!hasParticipantLayoutSpotlightTarget(latestParticipantsRef.current, roomSpotlightTileId)) {
        onRoomSpotlightChange(null);
      }
    }, getParticipantLayoutSpotlightCleanupDelay(participants, roomSpotlightTileId));

    return () => {
      clearTimeout(timeoutId);
    };
  }, [canManageRoomSpotlight, onRoomSpotlightChange, participants, roomSpotlightTileId]);

  const debugApi = useMemo(
    () => ({
      enable: enabledState.enable,
      disable: enabledState.disable,
      toggleEnabled: enabledState.toggleEnabled,
      ...debugControls
    }),
    [debugControls, enabledState.disable, enabledState.enable, enabledState.toggleEnabled]
  );

  return (
    <ParticipantLayoutRuntime
      enabled={layoutEnabled}
      mode={mode}
      participants={participants}
      showParticipants={showParticipants}
      mySessionId={mySessionId}
      zoomPinnedParticipant={zoomPinnedParticipant}
      onRequestClose={forcedEnabled ? undefined : enabledState.disable}
      debugApi={debugApi}
      requestParticipantMute={requestParticipantMute}
      toggleParticipantMuted={toggleParticipantMuted}
      toggleParticipantPinned={toggleParticipantPinned}
      toggleParticipantRoomSpotlight={toggleParticipantRoomSpotlight}
    />
  );
}

RoomParticipantLayoutContainer.propTypes = {
  enabled: PropTypes.bool,
  onEnabledChange: PropTypes.func,
  forcedEnabled: PropTypes.bool,
  entered: PropTypes.bool,
  presences: PropTypes.object,
  mySessionId: PropTypes.string,
  scene: PropTypes.object,
  hubChannel: PropTypes.object,
  performConditionalSignIn: PropTypes.func,
  roomSpotlightTileId: PropTypes.string,
  onRoomSpotlightChange: PropTypes.func,
  zoomPinnedParticipant: PropTypes.bool
};

export function ParticipantLayoutContainer(props) {
  if (canUseRoomParticipantLayoutState(props)) {
    return <RoomParticipantLayoutContainer {...props} />;
  }

  return <MockParticipantLayoutContainer {...props} />;
}

ParticipantLayoutContainer.propTypes = {
  enabled: PropTypes.bool,
  onEnabledChange: PropTypes.func,
  forcedEnabled: PropTypes.bool,
  entered: PropTypes.bool,
  presences: PropTypes.object,
  mySessionId: PropTypes.string,
  scene: PropTypes.object,
  hubChannel: PropTypes.object,
  performConditionalSignIn: PropTypes.func,
  roomSpotlightTileId: PropTypes.string,
  onRoomSpotlightChange: PropTypes.func,
  zoomPinnedParticipant: PropTypes.bool
};
